import React, { useEffect, useState } from 'react';
import { Col, Row, message } from 'antd';
import { UserOutlined, BarChartOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { PageTitle } from '../../components/common/PageTitle/PageTitle';
import { useResponsive } from '../../hooks/useResponsive';
import { useTranslation } from 'react-i18next';
import { useTypedSelector } from '../../hooks/useTypedSelector';
import { useActions } from '../../hooks/useActions';
import { useSearchParams, useParams, useNavigate } from 'react-router-dom';
import { notificationController } from '../../controllers/notificationController';

import { IStudentState } from '../../store/reducers/students/types';
import { StudentsTable, Student } from '../../components/common/tables';
import { StudentModal, DeleteConfirmModal } from '../../components/common/modals';
import { PageContainer, ContentCard } from '../../components/layouts/PageLayouts';
import { UploadStudentsCsv } from '../../components/common/UploadStudentsCsv';

// Styled components for class stats badges
const ClassStatsBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  padding: 14px 24px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 32px;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }
  
  .badge-icon {
    width: 36px;
    height: 36px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    color: white;
  }
  
  .badge-content {
    display: flex;
    align-items: center;
    gap: 7px;
    
    .badge-value {
      font-size: 20px;
      font-weight: 700;
      color: #1a1a2e;
    }
    
    .badge-label {
      font-size: 15px;
      color: rgba(0, 0, 0, 0.65);
      font-weight: 500;
    }
  }
`;

const ClassStatsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  justify-content: flex-end;
  margin-left: auto;
  
  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 16px;
    justify-content: flex-start;
    margin-left: 0;
  }
`;

const ClassTitle = styled.span`
  display: inline-flex;
  align-items: baseline;
  
  .class-prefix {
    color: rgba(0, 0, 0, 0.65);
    font-weight: 500;
  }
  
  .class-name {
    color: #1677ff;
    font-weight: 700;
    margin-left: 12px;
  }
`;

const ClassPage: React.FC = (props: any) => {
  const { isDesktop } = useResponsive();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const { className } = useParams<{ className: string }>();
  const students = useTypedSelector<any>(state => state.students);
  const { deleteStudentRequest, getClassRequest, createStudentRequest, updateStudentRequest } = useActions();
  const { success: deleteSuccess } = useTypedSelector<IStudentState>(state => state.students).delete;
  const { success: createSuccess } = useTypedSelector<IStudentState>(state => state.students).create || {};
  const { success: updateSuccess } = useTypedSelector<any>(state => state.students).update || {};
  
  // Modal states
  const [studentModal, setStudentModal] = useState({
    isOpen: false,
    mode: 'create' as 'create' | 'edit',
    student: null as Student | null
  });
  
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    studentId: null as number | null,
    studentName: ''
  });
  
  const [csvModal, setCsvModal] = useState(false);
  
  const [classData, setClassData] = useState<any>(null);

  useEffect(() => {
    // Add userId to URL if authenticated
    const storedUserId = localStorage.getItem("UserId");
    if (storedUserId && storedUserId !== 'null' && !searchParams.get('userId')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('userId', storedUserId);
      setSearchParams(newParams, { replace: true });
    }
    
    // Get class ID from URL params (fallback for direct navigation with ID)
    const classIdFromParams = searchParams.get("id");
    
    if (classIdFromParams) {
      const id = Number(classIdFromParams);
      getClassRequest(id);
      // Store for future reference
      localStorage.setItem("classId", classIdFromParams);
    } else {
      // Try to get from localStorage if no ID in params
      const storedClassId = localStorage.getItem("classId");
      if (storedClassId) {
        const id = Number(storedClassId);
        getClassRequest(id);
      }
    }
  }, [className, searchParams]);
  
  useEffect(() => {
    if (deleteSuccess == true) {
      const classIdFromParams = searchParams.get("id");
      const storedClassId = localStorage.getItem("classId");
      const classId = classIdFromParams || storedClassId;
      
      if (classId) {
        const id = Number(classId);
        getClassRequest(id);
      }
      
      // Close delete modal and show success message
      setDeleteModal({ isOpen: false, studentId: null, studentName: '' });
      notificationController.success({
        message: 'Student deleted successfully',
        description: `"${deleteModal.studentName}" has been removed from the class.`
      });
    }
  }, [deleteSuccess]);

  // Handle successful student creation
  useEffect(() => {
    if (createSuccess) {
      const classIdFromParams = searchParams.get("id");
      const storedClassId = localStorage.getItem("classId");
      const classId = classIdFromParams || storedClassId;
      
      if (classId) {
        const id = Number(classId);
        getClassRequest(id);
     }
      
      notificationController.success({
        message: 'Student added successfully',
        description: 'The student has been added to the class.'
      });
    }
  }, [createSuccess]);

  // Handle successful student update
  useEffect(() => {
    if (updateSuccess) {
      const classIdFromParams = searchParams.get("id");
      const storedClassId = localStorage.getItem("classId");
      const classId = classIdFromParams || storedClassId;
      
      if (classId) {
        const id = Number(classId);
        getClassRequest(id);
      }
      
      notificationController.success({
        message: 'Student updated successfully',
        description: 'Student information has been updated.'
      });
    }
  }, [updateSuccess]);

  // Student management handlers
  const handleAddStudent = () => {
    setStudentModal({
      isOpen: true,
      mode: 'create',
      student: null
    });
  };

  const handleEditStudent = (student: Student) => {
    setStudentModal({
      isOpen: true,
      mode: 'edit',
      student
    });
  };

  const handleDeleteStudent = (studentId: number) => {
    const student = students.students?.find((s: any) => s.id === studentId);
    const studentName = student?.firstName && student?.lastName 
      ? `${student.firstName} ${student.lastName}`.trim()
      : student?.name || `Student ${studentId}`;
    
    setDeleteModal({
      isOpen: true,
      studentId,
      studentName
    });
  };

  const handleStudentClick = (studentId: number) => {
    const encodedClassName = encodeURIComponent(decodedClassName.replace(/\s+/g, '-').toLowerCase());
    navigate(`/class/${encodedClassName}/${studentId}?id=${classId}`);
  };

  const handleStudentModalSubmit = (studentData: Partial<Student>) => {
    
    if (studentModal.mode === 'create') {
      // Преобразуем данные в формат CreateStudentModel
      const nameParts = (studentData.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const createStudentData = {
        firstName,
        lastName,
        classStudentId: `${Date.now()}`, // Генерируем уникальный ID
        classId: Number(classId),
        email: studentData.email || undefined,
        phone: studentData.phone || undefined,
        dateOfBirth: studentData.dateOfBirth || undefined
      };
      
      createStudentRequest(createStudentData);
    } else {
      // Преобразуем данные в формат UpdateStudentModel
      const nameParts = (studentData.name || '').split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';
      
      const updateStudentData = {
        id: studentData.id || 0,
        firstName,
        lastName,
        classStudentId: studentData.id?.toString() || '',
        classId: Number(classId),
        email: studentData.email || undefined,
        phone: studentData.phone || undefined,
        dateOfBirth: studentData.dateOfBirth || undefined
      };
      updateStudentRequest(updateStudentData);
    }
    setStudentModal({ isOpen: false, mode: 'create', student: null });
  };

  const handleStudentModalClose = () => {
    setStudentModal({ isOpen: false, mode: 'create', student: null });
  };

  const handleDeleteConfirm = () => {
    if (deleteModal.studentId) {
      deleteStudentRequest(deleteModal.studentId);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, studentId: null, studentName: '' });
  };

  const handleImportCSV = () => {
    setCsvModal(true);
  };

  const handleCsvModalClose = () => {
    setCsvModal(false);
  };

  const handleCsvImport = () => {
    // CSV import is handled by the UploadStudentsCsv component via API
    // After successful import, refresh the students list
    if (classId) {
      getClassRequest(Number(classId));
    }
    setCsvModal(false);
  };

  
  // Decode class name for display
  const decodedClassName = className ? decodeURIComponent(className).replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Class';
  
  // Get class ID from URL params or localStorage
  const classId = searchParams.get("id") || localStorage.getItem('currentClassId');
  
  // Calculate class statistics
  const totalStudents = students.students?.length || 0;
  const averageGrade = totalStudents > 0 
    ? Math.round(students.students.reduce((sum: number, student: any) => sum + (student.grade || 0), 0) / totalStudents)
    : 0;

  // Render class statistics badges
  const renderClassStats = () => (
    <ClassStatsContainer>
      <ClassStatsBadge>
        <div className="badge-icon" style={{ backgroundColor: '#52c41a' }}>
          <UserOutlined />
        </div>
        <div className="badge-content">
          <span className="badge-value">{totalStudents}</span>
          <span className="badge-label">Students</span>
        </div>
      </ClassStatsBadge>
      
      <ClassStatsBadge>
        <div className="badge-icon" style={{ backgroundColor: '#fa8c16' }}>
          <BarChartOutlined />
        </div>
        <div className="badge-content">
          <span className="badge-value">{averageGrade || 'N/A'}</span>
          <span className="badge-label">Avg Grade</span>
        </div>
      </ClassStatsBadge>
    </ClassStatsContainer>
  );
  
  // Transform students data for the table
  const studentsData: Student[] = students.students?.map((student: any) => {
    return {
      id: student.id,
      name: student.firstName && student.lastName 
        ? `${student.firstName} ${student.lastName}`.trim()
        : student.name || `Student ${student.id}`,
      email: student.email || undefined,
      phone: student.phone || undefined,
      dateOfBirth: student.dateOfBirth || undefined,
      grade: student.grade || undefined,
      lastMark: student.lastMark || student.lastExamGrade || undefined,
      lastActivity: student.lastActivity || student.updatedAt || student.createdAt || 'Never'
    };
  }) || [];
  
  return (
    <>
      <PageTitle title={`${decodedClassName} - Class Details`} description={`Manage students and activities for ${decodedClassName}`} url={window.location.href} noIndex={true}></PageTitle>
      
      <PageContainer
        title={
          <ClassTitle>
            <span className="class-prefix">Class</span>
            <span className="class-name">{decodedClassName}</span>
          </ClassTitle>
        }
        subtitle="Manage students and track their progress"
        titleStats={renderClassStats()}
      >
        <ContentCard>
          <StudentsTable
            students={studentsData}
            onAddStudent={handleAddStudent}
            onEditStudent={handleEditStudent}
            onDeleteStudent={handleDeleteStudent}
            onStudentClick={handleStudentClick}
            onImportCSV={handleImportCSV}
            loading={students.loading}
          />
        </ContentCard>
      </PageContainer>

      {/* Student Modal */}
      <StudentModal
        isOpen={studentModal.isOpen}
        onClose={handleStudentModalClose}
        onSubmit={handleStudentModalSubmit}
        student={studentModal.student}
        mode={studentModal.mode}
        isLoading={false}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Student"
        description="Are you sure you want to remove this student from the class?"
        itemName={deleteModal.studentName}
        isLoading={students.delete?.loading || false}
      />

      <UploadStudentsCsv
        visible={csvModal}
        onClose={handleCsvModalClose}
        onImport={handleCsvImport}
        classId={Number(classId)}
      />
    </>
  );
};

export default ClassPage